/* ==========================================================================
   A36 — theme.js

   IMPORTANTE: el editor de temas de Shopify vuelve a renderizar la sección
   completa cada vez que escribes en un campo. Por eso TODO lo que aquí se
   inicializa se hace por sección y se vuelve a ejecutar con los eventos
   shopify:section:load / :select. Si no, los elementos .reveal regresan del
   servidor con opacity:0 y el texto recién escrito "desaparece".
   ========================================================================== */
(function () {
  'use strict';

  var inEditor = !!(window.Shopify && window.Shopify.designMode);
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  /* ------------------------------------------------------ Revelado al scroll */
  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) { return revealObserver; }
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    return revealObserver;
  }

  function initReveal(root) {
    var els = root.querySelectorAll('.reveal');
    if (!els.length) { return; }

    /* En el editor, o sin soporte, o con movimiento reducido: visible de una vez. */
    if (inEditor || reduceMotion || !('IntersectionObserver' in window)) {
      each(els, function (el) { el.classList.add('is-visible'); });
      return;
    }

    var io = getRevealObserver();
    each(els, function (el) { io.observe(el); });

    /* Red de seguridad: lo que siga oculto y ya esté en pantalla, se muestra. */
    setTimeout(function () {
      each(root.querySelectorAll('.reveal:not(.is-visible)'), function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight) {
          el.classList.add('is-visible');
        }
      });
    }, 2000);
  }

  /* ------------------------------------------------------------- Menú móvil */
  function initNav(root) {
    var toggle = root.querySelector('[data-menu-toggle]');
    var nav = root.querySelector('[data-nav]');
    if (!toggle || !nav || toggle.dataset.bound) { return; }
    toggle.dataset.bound = '1';

    function close() {
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', function () {
      var open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.style.overflow = open ? 'hidden' : '';
    });

    each(nav.querySelectorAll('a'), function (link) {
      link.addEventListener('click', close);
    });

    /* Botón "Cerrar" dentro del panel a pantalla completa */
    each(root.querySelectorAll('[data-menu-close]'), function (btn) {
      btn.addEventListener('click', function () { close(); toggle.focus(); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) { close(); toggle.focus(); }
    });

    /* Al abrir, el foco pasa al primer enlace del menú. */
    toggle.addEventListener('click', function () {
      if (!nav.classList.contains('is-open')) { return; }
      var first = nav.querySelector('.nav-list a');
      if (first) { setTimeout(function () { first.focus(); }, 60); }
    });

    /* Si la ventana crece hasta el modo barra horizontal, se cierra solo. */
    window.addEventListener('resize', function () {
      var horizontal = window.matchMedia('(min-width:1041px)').matches &&
        !document.querySelector('.site-header.menu-drawer-always');
      if (horizontal && nav.classList.contains('is-open')) { close(); }
    });
  }

  /* ------------------------------------------------------------- Ticker */
  function initTicker(root) {
    each(root.querySelectorAll('[data-ticker-track]'), function (track) {
      if (track.children.length && !track.dataset.duplicated) {
        track.innerHTML += track.innerHTML;
        track.dataset.duplicated = '1';
      }
    });
  }

  /* ------------------------------------------------- Conteo animado de cifras */
  function initCounters(root) {
    var counters = root.querySelectorAll('[data-count]');
    if (!counters.length) { return; }

    /* En el editor no se anima: si no, cada tecla reinicia el conteo y
       parece que el número que estás escribiendo se borra. */
    if (inEditor || reduceMotion || !('IntersectionObserver' in window)) { return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) { return; }
        io.unobserve(entry.target);

        var el = entry.target;
        var raw = (el.getAttribute('data-count') || '').trim();
        var target = parseFloat(raw.replace(/[^0-9.]/g, ''));
        if (isNaN(target)) { return; }

        var hasComma = raw.indexOf(',') > -1;
        var decimals = (raw.split('.')[1] || '').length;
        var start = null;

        function frame(ts) {
          if (start === null) { start = ts; }
          var p = Math.min((ts - start) / 1200, 1);
          var value = (target * (1 - Math.pow(1 - p, 3))).toFixed(decimals);
          el.textContent = hasComma
            ? Number(value).toLocaleString('es-MX', { minimumFractionDigits: decimals })
            : value;
          if (p < 1) { requestAnimationFrame(frame); } else { el.textContent = raw; }
        }
        requestAnimationFrame(frame);
      });
    }, { threshold: 0.4 });

    each(counters, function (el) { io.observe(el); });
  }

  /* ------------------------------------------- Altura real del encabezado */
  function initHeaderHeight() {
    var header = document.querySelector('[data-header]');
    if (!header) { return; }
    var set = function () {
      document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
    };
    set();
    if (!window.__a36HeaderBound) {
      window.__a36HeaderBound = true;
      window.addEventListener('resize', set);
    }
  }

  /* --------------------------------------------------------------- Arranque */
  function init(root) {
    root = root || document;
    initReveal(root);
    initNav(root);
    initTicker(root);
    initCounters(root);
    initHeaderHeight();
  }

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  ready(function () { init(document); });

  /* Eventos del editor de temas: la sección se reemplaza entera al escribir. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:select', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) { init(e.target.closest ? (e.target.closest('.shopify-section') || e.target) : e.target); });

  /* Último cinturón de seguridad: si por cualquier motivo alguna sección
     queda oculta tras un re-render, se revela sola. */
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType === 1 && node.querySelector && node.querySelector('.reveal')) {
            init(node);
          }
        }
      }
    });
    ready(function () { mo.observe(document.body, { childList: true, subtree: true }); });
  }
})();
